import React from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import orderBy from "lodash/orderBy";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { useQuery } from "@apollo/client/react";

import { useFieldSettings } from "../../../contexts/customFields";
import useFields from "../../../hooks/useFields";
import useLabels from "../../../hooks/useLabels";
import { CUSTOM_FIELD_TEMPLATES_QUERY } from "../../../constants/graphql/queries";

export default function SortDropdown({ order, setOrder, project }) {
  const display = useFieldSettings(project);
  const fields = useFields();
  const l = useLabels();
  const { data: customFieldData } = useQuery(CUSTOM_FIELD_TEMPLATES_QUERY);
  const customFieldTemplates = customFieldData?.customFieldTemplates || [];

  const enabledFields = orderBy(
    fields
      .flatMap(v => v.list.map(([id, , label]) => ({ id, label, enabled: display[id] })))
      .filter(v => !["photo", "attachments", "barcode", "barcode_number"].includes(v.id))
      .concat({
        id: "barcode",
        label: l.identifiers.barcode_number,
        enabled: display.barcode_number,
      })
      .filter(v => v.enabled)
      .concat(customFieldTemplates
        .filter(t => display[`custom_field_${t.id}`])
        .map(t => ({
          id: `custom_field_${t.id}`,
          label: t.defaultLabel,
          enabled: true,
        }))),
    "label",
    "asc"
  );

  const selectedField = order?.split(".")[0];
  const selectedOrder = order?.split(".")[1];
  const selectedItem = enabledFields.find(v => v.id === selectedField);

  return (
    <PopupState variant="popover" popupId="sort-dropdown">
      {sortPopupState => (
        <>
          <Button
            variant="outlined"
            color="inherit"
            endIcon={<KeyboardArrowDownIcon />}
            {...bindTrigger(sortPopupState)}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              color: "text.primary",
              borderColor: "divider",
              "&:hover": {
                borderColor: "text.secondary",
              },
            }}
          >
            Sort: {selectedItem ? selectedItem.label : "Item ID"}
          </Button>
          <Menu
            {...bindMenu(sortPopupState)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{ mt: 0.5 }}
          >
            {enabledFields.map(({ id, label }) => (
              <MenuItem
                key={id}
                selected={selectedField === id}
                onClick={() => {
                  const newOrder = selectedField === id && selectedOrder === "desc" ? "asc" : "desc";
                  setOrder(`${id}.${newOrder}`);
                  sortPopupState.close();
                }}
              >
                <Typography sx={{ flexGrow: 1 }}>
                  {label}
                </Typography>
                {selectedField === id && (
                  selectedOrder === "asc" ? (
                    <ArrowUpwardIcon sx={{ ml: 1, color: "text.secondary" }} />
                  ) : (
                    <ArrowDownwardIcon sx={{ ml: 1, color: "text.secondary" }} />
                  )
                )}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </PopupState>
  );
}
